// ClickTracker — single delegated listener on the document that fires
// a small POST to /analytics/click whenever a marketing-page visitor
// clicks a button, link, or [data-testid] element. Owner-admin and
// signer routes are skipped (staff & signers aren't interesting for
// funnel analytics and would inflate the numbers).
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Skip click tracking on these route prefixes. Matches the "not the
// public marketing surface" list — same shape as ChatWidgetController.
const SKIP_PREFIXES = [
  '/owner-admin', '/sign/', '/portal/', '/portals/',
  '/dashboard', '/settings', '/inbox', '/invoices',
];

function getVisitorId() {
  try {
    let v = localStorage.getItem('cb_vid');
    if (!v) {
      v = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem('cb_vid', v);
    }
    return v;
  } catch {
    return 'v_anon';
  }
}

// Pick the best "what did the user click on" label — prefers explicit
// analytics annotation, then testid, then visible text, then href.
function labelFor(el) {
  const analyticsLabel = el.closest('[data-analytics]');
  if (analyticsLabel) return analyticsLabel.getAttribute('data-analytics').slice(0, 120);
  const testid = el.closest('[data-testid]');
  if (testid) return testid.getAttribute('data-testid').slice(0, 120);
  const text = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120);
  if (text) return text;
  const href = el.getAttribute && el.getAttribute('href');
  return href ? `link:${href.slice(0, 100)}` : 'unlabeled';
}

export default function ClickTracker() {
  const location = useLocation();

  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => location.pathname.startsWith(p))) return undefined;

    const handler = (e) => {
      const target = e.target.closest('a, button, [role="button"], [data-testid]');
      if (!target) return;
      const label = labelFor(target);
      const href = target.tagName === 'A' ? target.getAttribute('href') : null;
      const kind = target.tagName === 'A' ? 'link' : 'button';
      const body = JSON.stringify({
        path: location.pathname,
        label,
        target: href,
        kind,
        visitor_id: getVisitorId(),
      });
      // keepalive so the beacon survives if the click navigates away.
      fetch(`${API}/api/analytics/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body,
      }).catch(() => {});
    };

    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [location.pathname]);

  return null;
}
